import { forwardRef, useState, useRef } from "react"
import { createPortal } from "react-dom"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { CaretRight } from "@carbon/icons-react"
import { CIFType, Metric } from "../../states"
import { Typography } from "../Shadcn/Typography"

interface PopupProps {
  type: CIFType | "catchments" | "subregionBoundaries"
  isChecked: boolean
  isDisabled?: boolean
  selMetric: Metric
  metrics: Metric[]
  isOpen: boolean
  infoText: string | null
  handleOpen: () => void
  setSelMetric: any
}

export const OverlayMetricPopup = forwardRef<HTMLDivElement, PopupProps>(
  ({ type, isDisabled, selMetric, metrics, isOpen, infoText, handleOpen, setSelMetric }, ref) => {
    const caretRef = useRef<HTMLDivElement | null>(null)
    const [position, setPosition] = useState({ top: 0, left: 0 })

    const handleChangeMetrics = (item: Metric) => {
      setSelMetric((prev: any) => {
        if (type === "PoI" || type === "CTV" || type === "EH") {
          const update = { ...prev, [type]: { ...item, isRank: true } }
          localStorage.setItem("selMetrics", JSON.stringify(update))
          return update
        }
        return item
      })
      handleOpen()
    }

    const handlepopupOpen = () => {
      handleOpen()
      if (caretRef.current) {
        const rect = caretRef.current.getBoundingClientRect()
        setPosition({ top: rect.top + rect.height / 2, left: rect.right })
      }
    }

    return (
      <>
        {(infoText || metrics?.length > 0) && (
          <div
            className={`flex items-center gap-4 w-full min-w-0 ${
              isDisabled && type !== "subregionBoundaries" ? "cursor-default opacity-50" : "cursor-pointer"
            }`}
            onClick={metrics?.length > 1 && !(isDisabled && type !== "subregionBoundaries") ? () => handlepopupOpen() : undefined}
          >
            {infoText && (
              <Typography variant="b3" className="text-[var(--ds-solid-neutral-11)] truncate flex-1 min-w-0">
                {infoText}
              </Typography>
            )}
            {metrics?.length > 1 && (
              <div ref={caretRef} className="shrink-0">
                <CaretRight
                  size={12}
                  className={`icon-gray-0 p-0 ${
                    isDisabled && type !== "subregionBoundaries"
                      ? "cursor-default opacity-50 pe-none"
                      : "cursor-pointer"
                  }`}
                />
              </div>
            )}
          </div>
        )}

        {isOpen && createPortal(
          <div
            ref={ref}
            className="flex flex-col items-start py-[2px] px-[2px] gap-[2px] rounded-[var(--semantic-rounded-lg)] bg-[var(--general-molecules-bg-bg-white)] fixed z-[9999]"
            style={{
              top: position.top,
              left: position.left,
              transform: "translateY(-50%)",
              boxShadow: "var(--md-shadow-1-x, 0) var(--md-shadow-1-y, 4px) var(--md-shadow-1-blur, 6px) var(--md-shadow-1-spread, -1px) var(--md-color, rgba(0,0,0,0.10)), var(--md-shadow-2-x, 0) var(--md-shadow-2-y, 2px) var(--md-shadow-2-blur, 4px) var(--md-shadow-2-spread, -2px) var(--md-color, rgba(0,0,0,0.10))",
            }}
          >
            <RadioGroupPrimitive.Root
              value={selMetric?.key ?? ""}
              onValueChange={(val) => {
                const item = metrics.find((m) => m.key === val)
                if (item) handleChangeMetrics(item)
              }}
              className="flex flex-col gap-[2px] w-full"
            >
              {metrics?.map((item) => (
                <label key={item.key} className="flex h-8 px-[var(--semantic-xs)] items-center gap-[var(--semantic-xs)] self-stretch cursor-pointer hover:bg-muted transition-colors justify-between">
                  <div className="flex items-center gap-[var(--semantic-xs)] min-w-0">
                    <RadioGroupPrimitive.Item
                      value={item.key}
                      className="shrink-0 size-[0.875rem] rounded-full border border-[#c5c9d0] bg-white flex items-center justify-center cursor-pointer focus-visible:outline-none"
                    >
                      <RadioGroupPrimitive.Indicator className="flex items-center justify-center w-full h-full">
                        <span className="block rounded-full bg-[var(--general-text-primary,#18202b)] size-[0.375rem]" />
                      </RadioGroupPrimitive.Indicator>
                    </RadioGroupPrimitive.Item>
                    <Typography
                      variant="b2"
                      className="font-sans font-normal leading-[var(--line-height-s-heading)] tracking-[var(--letter-spacing-default)] text-[var(--general-text-primary)] truncate"
                    >
                      {item.title}{"count" in item ? ` (${item.count})` : ""}
                    </Typography>
                  </div>
                  {item.icon && <img src={item.icon} alt={item.title} className="shrink-0" />}
                </label>
              ))}
            </RadioGroupPrimitive.Root>
          </div>,
          document.body
        )}
      </>
    )
  }
)

OverlayMetricPopup.displayName = "OverlayMetricPopup"
